import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, BarChart3, Zap, HeadphonesIcon, Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'crm',
      icon: Users,
      title: t('services.crm.title'),
      desc: t('services.crm.desc'),
      features: t('services.crm.features'),
      color: '#007AFF',
      bgColor: '#007AFF10',
    },
    {
      id: 'marketing',
      icon: BarChart3,
      title: t('services.marketing.title'),
      desc: t('services.marketing.desc'),
      features: t('services.marketing.features'),
      color: '#34C759',
      bgColor: '#34C75910',
    },
    {
      id: 'sales',
      icon: Zap,
      title: t('services.sales.title'),
      desc: t('services.sales.desc'),
      features: t('services.sales.features'),
      color: '#FF9500',
      bgColor: '#FF950010',
    },
    {
      id: 'service',
      icon: HeadphonesIcon,
      title: t('services.service.title'),
      desc: t('services.service.desc'),
      features: t('services.service.features'),
      color: '#AF52DE',
      bgColor: '#AF52DE10',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-semibold text-[#1D1D1F] tracking-tight leading-[1.1] mb-6 font-['Plus_Jakarta_Sans']">
              {t('services.title')}
            </h1>
            <p className="text-lg md:text-xl text-[#86868B] leading-relaxed">
              {t('services.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="space-y-16 md:space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: service.bgColor }}
                  >
                    <service.icon className="w-8 h-8" style={{ color: service.color }} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#86868B] leading-relaxed mb-8">
                    {service.desc}
                  </p>
                  <ul className="space-y-4 mb-8">
                    {Array.isArray(service.features) && service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: service.bgColor }}
                        >
                          <Check className="w-4 h-4" style={{ color: service.color }} />
                        </div>
                        <span className="text-[#1D1D1F]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button
                      className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-8 py-3 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                      data-testid={`service-cta-${service.id}`}
                    >
                      {t('hero.cta')}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div
                    className="aspect-[4/3] rounded-3xl overflow-hidden"
                    style={{ backgroundColor: service.bgColor }}
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <service.icon
                        className="w-32 h-32 opacity-20"
                        style={{ color: service.color }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            {...fadeUp}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
              {t('hero.tagline')}
            </h2>
            <p className="text-lg text-[#86868B] mb-10">
              {t('hero.subtitle')}
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-10 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                data-testid="services-cta-btn"
              >
                {t('hero.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
